import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'

import { useKitlookSkuMenuPosition } from '../hooks/useKitlookSkuMenuPosition'

export interface KitlookSkuOption {
  value: string
  skuId: string
  position: number | undefined
}

interface KitlookSkuOptionsMenuProps {
  triggerRef: RefObject<HTMLElement | null>
  options: KitlookSkuOption[]
  selectedSku: string | null
  onSelect: (skuId: string) => void
  onClose: () => void
}

export function KitlookSkuOptionsMenu({
  triggerRef,
  options,
  selectedSku,
  onSelect,
  onClose,
}: KitlookSkuOptionsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const position = useKitlookSkuMenuPosition({
    triggerRef,
    menuRef,
    listRef,
    optionsCount: options.length,
  })

  useEffect(() => {
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const { target } = event

      if (!(target instanceof Node)) return
      if (triggerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return

      onClose()
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose, triggerRef])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={menuRef}
      data-testid="kitlook-sku-options"
      className={classNames(
        'fixed z-[100000] overflow-auto overscroll-contain border border-checkoutInputBorder bg-white shadow-lg',
        position?.placement === 'up' ? 'rounded-t-md' : 'rounded-b-md',
        !position && 'invisible pointer-events-none'
      )}
      style={
        position
          ? {
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              ...(position.placement === 'down'
                ? { top: position.top }
                : { bottom: position.bottom }),
            }
          : undefined
      }
    >
      <ul ref={listRef} className="flex flex-col py-1">
        {options.map(({ value, skuId, position: optionOrder }) => (
          <li key={`kitlook-opt-${skuId}`} style={{ order: optionOrder }}>
            <button
              type="button"
              className={`flex w-full items-center px-4 py-3 text-sm font-normal hover:bg-[#F7F8F9] ${
                selectedSku === skuId
                  ? 'bg-[#F7F8F9] font-semibold text-primaryBlue'
                  : 'text-restructure-primary'
              }`}
              onClick={() => onSelect(skuId)}
            >
              {value}
            </button>
          </li>
        ))}
      </ul>
    </div>,
    document.body
  )
}
